import { useEffect, useState } from 'react';
import { useLocale, useQuery, type Option } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useModalState } from 'hooks/useModalState/useModalState';
import { fetchServers } from 'api/servers/servers';
import { QueryKeyEnum } from 'core/global.enum';
import { fetchTerminals } from 'api/terminals/terminals';
import { GroupTerminalsPermissionsIdSortEnum } from 'context/query/queryContext/QueryContext.enum';
import { AppMessages } from 'i18n/messages';
import { downloadInneEventsReport } from 'api/reports/reports';
import { useReportDownload } from '../hooks/useReportDownload';
import { useSmtpConfiguration } from '../hooks/useSmtpConfiguration';
import { EmailSelectionModalContainer } from '../emailSelectionModal';

import { useInneEventsReportValidation } from './InneEventsReport.validation';
import { InneEventsReportForm } from './InneEventsReport.types';
import { InneEventsReport } from './InneEventsReport';

export const InneEventsReportContainer = () => {
  const { isModalOpen, onModalClose, onModalOpen } = useModalState();
  const {
    isModalOpen: isEmailModalOpen,
    onModalClose: onEmailModalClose,
    onModalOpen: onEmailModalOpen,
  } = useModalState();
  const { formatMessage } = useLocale();
  const [selectedEmails, setSelectedEmails] = useState<Option[]>([]);
  const { isSmtpConfigured } = useSmtpConfiguration();

  const { schema } = useInneEventsReportValidation();
  const formMethods = useForm<InneEventsReportForm>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const { serwerId } = formMethods.watch();

  const { data: serversResponse } = useQuery([QueryKeyEnum.SERVERS], fetchServers);
  const servers = serversResponse?.data || [];

  const { data: terminalsResponse, isInitialLoading: isLoadingTerminals } = useQuery(
    [QueryKeyEnum.TERMINALS, serwerId],
    () => fetchTerminals(serwerId),
    {
      args: {
        sort: [GroupTerminalsPermissionsIdSortEnum.ASC],
      },
      enabled: !!serwerId,
    },
  );
  const terminals = terminalsResponse?.data || [];

  const {
    onDownload: onDownloadInneEventsReport,
    isLoading: isLoadingDownload,
    hasNoDataError,
    hasNetworkError,
    resetError,
  } = useReportDownload({
    mutationFn: downloadInneEventsReport,
    buildRequestBody: (emails) => {
      const formValues = formMethods.watch();
      return {
        numerKarty: formValues.numerKarty,
        dataOd: formValues.dataOd?.toISOString().split('T')[0],
        dataDo: formValues.dataDo?.toISOString().split('T')[0],
        kategoria: formValues.kategoria,
        serwer: formValues.serwerId,
        tkds: formValues.tkds?.map((tkd) => tkd.value.toString()),
        // Backend currently filters by a single `czytnik` only - keep it for the one-reader case
        czytnik: formValues.tkds?.length === 1 ? formValues.tkds[0].value.toString() : undefined,
        selectedEmails: emails,
      };
    },
    onEmailModalClose,
    reportNameId: AppMessages['reports.inneEventsReport.title'],
  });

  const handleEmailModalOpen = () => {
    resetError();
    onEmailModalOpen();
  };

  const handleConfirmEmails = (emails: string[]) => {
    setSelectedEmails(emails.map((email) => ({ value: email, label: email })));
    onDownloadInneEventsReport(emails);
  };

  useEffect(() => {
    if (isModalOpen) {
      formMethods.reset({
        serwerId: servers[0]?.id,
      });
    } else {
      resetError();
      setSelectedEmails([]);
    }
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <InneEventsReport
        servers={servers}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        terminals={terminals}
        isLoadingTerminals={isLoadingTerminals}
        onDownload={onDownloadInneEventsReport}
        isLoadingDownload={isLoadingDownload}
        hasNoDataError={hasNoDataError}
        hasNetworkError={hasNetworkError}
        onEmailModalOpen={handleEmailModalOpen}
        isEmailModalOpen={isEmailModalOpen}
        isSmtpConfigured={isSmtpConfigured}
        onResetError={resetError}
      />
      {isModalOpen && (
        <EmailSelectionModalContainer
          title={formatMessage({ id: AppMessages['reports.inneEventsReport.title'] })}
          open={isEmailModalOpen}
          onClose={onEmailModalClose}
          onConfirm={handleConfirmEmails}
          hasNoDataError={hasNoDataError}
          onResetError={resetError}
          selectedUsers={selectedEmails}
        />
      )}
    </FormProvider>
  );
};
